/**
 * The "+" on a balance chip is a spot on one button, not a second control:
 * the chip opens the store either way, and the disc answers a press on IT
 * with its own beat (it swells and glows) so the two presses read
 * differently — the owner: "if you press the plus, it will have a different
 * visual feedback than if you press the entire thing".
 *
 * The disc is the chip's `::after` box, drawn by hub.css / pills.css, so a
 * press on it is a pointer landing in that box's share of the chip's right
 * end. A keyboard activation has no pointer and is never "the plus".
 */

/**
 * @param {HTMLElement | null} chip
 * @param {MouseEvent | PointerEvent | null | undefined} event
 * @returns {boolean}
 */
export function isPlusPress(chip, event) {
    if (!chip || !event || typeof event.clientX !== 'number') return false;
    // A synthesised click (Enter/Space, `.click()`) reports 0,0 and no detail.
    if (event.clientX === 0 && event.clientY === 0 && !event.detail) return false;
    let after;
    try {
        after = window.getComputedStyle(chip, '::after');
    } catch (_) {
        return false;
    }
    const content = after?.content;
    if (!content || content === 'none' || content === 'normal') return false;
    const width = parseFloat(after.width) || 0;
    if (!width) return false;
    const box = chip.getBoundingClientRect();
    const paddingRight = parseFloat(window.getComputedStyle(chip).paddingRight) || 0;
    const plusLeft = box.right - paddingRight - width - 4;
    return event.clientX >= plusLeft && event.clientX <= box.right + 1;
}

const PULSE_MS = 420;
const pulses = new WeakMap();

/** The disc's own press beat: restart it on every press. */
export function pulsePlus(chip) {
    if (!chip) return false;
    chip.classList.remove('is-plus-pressed');
    void chip.offsetWidth;
    chip.classList.add('is-plus-pressed');
    const previous = pulses.get(chip);
    if (previous) clearTimeout(previous);
    pulses.set(chip, setTimeout(() => {
        chip.classList.remove('is-plus-pressed');
        pulses.delete(chip);
    }, PULSE_MS));
    return true;
}
